<template>
  <div v-if="histModel.length > 1" class="d-flex align-items-center mb-3 gap-3">
    <div style="width: 10em">
      <label class="form-label mb-1">Автор</label>
      <MultiSelectDropdown
        :options="authorsList"
        v-model="selectedAuthors"
        buttonWidth="10em"
      />
    </div>
    <div>
      <label class="form-label mb-1">Дата с</label>
      <DatePicker v-model="startDate" />
    </div>
    <div>
      <label class="form-label mb-1">Дата по</label>
      <DatePicker v-model="endDate" />
    </div>
    <div class="align-self-end">
      <button class="btn btn-outline-secondary" @click="resetFilters">Сбросить</button>
    </div>
  </div>
  <div class="history-main">
    <table class="table table-sm history">
      <tbody>
        <template v-for="step in isAuditTrail ? paginatedModel : filteredModel" :key="step.id">
          <tr v-if="step.changes.length">
            <td :rowspan="step.changes.length" class="fw-semibold">{{ step.author }}</td>
            <td :rowspan="step.changes.length" class="text-muted">{{ step.date }}</td>
            <td :rowspan="step.changes.length">
              <span v-if="step.entity" v-html="step.entity"></span>
            </td>
            <td class="text-nowrap">
              <span v-if="step.changes[0].action" v-html="step.changes[0].action"></span>
            </td>
            <td style="min-width: 250px">
              <span v-if="step.changes[0].text" v-html="step.changes[0].text"></span>
            </td>
          </tr>

          <tr v-for="(chg, idx) in step.changes.slice(1)" :key="`${step.id}-${idx + 1}`">
            <td class="text-nowrap">
              <span v-if="chg.action" v-html="chg.action"></span>
            </td>
            <td style="min-width: 250px">
              <span v-if="chg.text" v-html="chg.text"></span>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <Pagination
      v-if="isAuditTrail"
      :totalItem="filteredModel.length"
      :currentPage="currentPage"
      :onPage="onPage"
      @pageChange="handlePageChange"
    />
  </div>
</template>

<script setup>
import { computed, isReactive, isRef, toValue, ref, watch } from 'vue'
import DOMPurify from 'dompurify'
import { get as _get, result as _result } from 'lodash-es'
import { models, fields, values } from '@/lib/history'
import { commons } from '@provise/lib/transformer'
import MultiSelectDropdown from '@/components/dropdowns/MultiSelectDropdown.vue'
import DatePicker from '@provise/components/datepicker.vue'
import Pagination from '@provise/components/pagination/pagination.vue'
import { parse } from 'date-fns'
const props = defineProps({
  list: Object,
  limit: { type: Number, default: null },
  modelName: String,
  modelId: Number,
})
const histModel = computed(() => _pick(props.list))

const currentPage = ref(1)
const onPage = ref(20)
defineExpose({
  resetFilters,
})

const handlePageChange = ({ page, onPage: newOnPage }) => {
  currentPage.value = page
  onPage.value = Number(newOnPage)
}

const isAuditTrail = computed(() => props.modelName === 'audit_trail')
const selectedAuthors = ref([])
const startDate = ref(null)
const endDate = ref(null)

const authorsList = computed(() => {
  const uniq = Array.from(new Set(histModel.value.map(s => s.author)))
  return uniq.sort().map(a => ({ value: a, label: a }))
})

function resetFilters() {
  selectedAuthors.value = []
  startDate.value = null
  endDate.value = null
  currentPage.value = 1
}

watch([selectedAuthors, startDate, endDate], () => {
  currentPage.value = 1
})

const parseStepDate = str => parse(str, 'dd.MM.yyyy, HH:mm', new Date())

const filteredModel = computed(() => {
  return histModel.value.filter(step => {
    if (selectedAuthors.value.length > 0 && !selectedAuthors.value.includes(step.author)) {
      return false
    }
    const d = parseStepDate(step.date)
    if (startDate.value) {
      const from = new Date(startDate.value)
      from.setHours(0, 0, 0)
      if (d < from) return false
    }
    if (endDate.value) {
      const to = new Date(endDate.value)
      to.setHours(23, 59, 59)
      if (d > to) return false
    }
    return true
  })
})

const paginatedModel = computed(() => {
  const start = (currentPage.value - 1) * onPage.value
  const end = start + onPage.value
  return filteredModel.value.slice(start, end)
})

function formatDiff(ch) {
  let rel = _result(this, [ch.model_name, ch.model_id])
  if (!rel && Array.isArray(this[ch.model_name])) {
    rel = this[ch.model_name].find(d => d.id === ch.model_id)
  }
  const fmt = { entity: null, key: `${ch.model_name}:${ch.model_id}` }
  const changes = []

  switch (ch.model_name) {
    case 'Activities':
    case 'ActivityTypes':
    case 'Comments':
    case 'ConsumablesExpenses':
    case 'ConsumablesPurchases':
    case 'ConsumablesStock':
    case 'DevicesExpenses':
    case 'DevicesPurchases':
    case 'DevicesStock':
    case 'MaterialsComm':
    case 'MaterialsCommExpenses':
    case 'MaterialsCommPurchases':
    case 'MaterialsCommStock':
    case 'MaterialsNd':
    case 'MaterialsNdExpenses':
    case 'MaterialsNdPurchases':
    case 'MaterialsNdStock':
    case 'Medicines':
    case 'MedicinesExpenses':
    case 'MedicinesPurchases':
    case 'MedicinesStock':
    case 'MedicineTrademarks':
    case 'Orders':
    case 'Templates':
    case 'RequestCategory':
    case 'CustomerCategory':
    case 'DocTemplates':
    case 'EmailTemplates':
    case 'Uploads':
    case 'SLA':
    case 'OfficialReplies':
    case 'Transfers':
    case 'User':
    case 'Dropdowns':
    case 'DropdownsCatalog':
      fmt.entity = models[ch.model_name].entityName(ch, rel, isAuditTrail.value)
      break
    case 'EmailAddress':
    case 'Communication':
    case 'Departments':
    case 'Complaints':
      fmt.entity = models[ch.model_name].entityName(ch, this, isAuditTrail.value)
      break
    default:
      fmt.entity = `Сущность ${ch.model_name}:${ch.model_id}`
  }

  const excludeFields = [
    'vortex_log',
    'approval_comment',
    'commerce_declined_by',
    'declined_by'
  ]
  const showIterable = [
    '_subcomplaints',
    'roles'
  ]

  for (const change of ch.changes) {
    const cfmt = {}

    switch (change.action) {
      case 'values_changed':
      case 'type_changes':
        if (excludeFields.includes(change.path[0])) continue

        cfmt.field = _get(fields, [ch.model_name, ...change.path]) || _get(fields, change.path)
        if (!(cfmt.field instanceof Function)) {
          if (!cfmt.field) {
            cfmt.field = change.path.join('.')
          }

          cfmt.action = '<span class="text-muted">изменено поле:</span>'
          cfmt.val = DOMPurify.sanitize(
            `<span class="text-success d-inline-block">${getVal(ch, change, this)}</span>`
          )
          cfmt.old_val = null
          if (getVal(ch, change, this, 'old_value'))
            cfmt.old_val = DOMPurify.sanitize(
              `<span class="text-danger d-inline-block">${getVal(ch, change, this, 'old_value')}</span>`
            )
        }

        break
      case 'add_object':
        cfmt.action = '<span class="text-muted">добавлено</span>'
        break
      case 'delete_object':
        cfmt.action = '<span class="text-muted">удалено</span>'
        break
      case 'iterable_item_removed':
      case 'iterable_item_added':
        let path = Array.isArray(change.path) ? change.path[0] : change.path
        if (!showIterable.includes(path)) continue
        let ival = getVal(ch, change, this)
        try {
          const parsed = JSON.parse(ival);
          if (Array.isArray(parsed)) {
              ival = null;
            } 
        } catch {
          ival = ival
        }
        if (!ival) continue
        cfmt.iterable = true
        cfmt.field = _get(fields, [ch.model_name, ...path]) || _get(fields, path)
        cfmt.val = DOMPurify.sanitize(
          `<span class="text-muted d-inline-block">${ival}</span>`
        )
        if (change.action == 'iterable_item_removed') {
          cfmt.action = '<span class="text-muted">удалены</span>'
        } else {
          cfmt.action = '<span class="text-muted">добавлены</span>'
        }
        break
      case 'declined':
        if (ch.model_name !== 'OfficialReplies')
          cfmt.action = '<span class="text-muted">отклонено</span>'
        break
      case 'closed':
        cfmt.action = '<span class="text-muted">закрыто</span>'
        break
      default:
        cfmt.action = ''
    }

    const str = []

    if (cfmt.field instanceof Function) {
      if (!change.relation) {
        change.relation = findClass(this?.map || [], ch.model_name, change.path?.[0] || '')
      }
      const _str = cfmt.field(change, this)
      if (_str) {
        str.push(_str)
      }
    } else {
      //str.push(cfmt.action)
      if (cfmt.field) {
        if (cfmt.field == change.path?.[0]) {
          const req_field = searchFieldset(this, cfmt.field)
          if (req_field) {
            cfmt.field = req_field
          }
        }
        str.push('"' + cfmt.field + '"')
      }
      if (cfmt.old_val) {
        str.push('с ' + cfmt.old_val + '')
      }
      if (cfmt.val) {
        if (cfmt.iterable) {
          str.push('- ' + toValue(cfmt.val) + '')
        } else {
          str.push('на ' + toValue(cfmt.val) + '')
        }
      }
    }

    changes.push({ action: cfmt.action, text: str.join(' ') })
  }
  fmt.changes = changes
  return fmt
}

function searchFieldset(data, fieldName) {
  if (!data.RequestCategory) {
    return null
  }
  const request_data = data.RequestCategory

  const mergeFieldsets = request_data => {
    return request_data.reduce((acc, category) => {
      return { ...acc, ...category.fieldset }
    }, {})
  }

  const findFieldValue = fieldName => {
    const mergedFieldset = mergeFieldsets(request_data)
    return mergedFieldset[fieldName] || null
  }

  return findFieldValue(fieldName)
}

function getVal(ch, change, rel, prop = 'value') {
  const v = values()
  let res = _get(v, [ch.model_name, ...change.path]) || _get(v, change.path)
  if (!res) {
    res = () => change[prop]
  }
  let val = res(ch, change, rel, prop)
  if (isRef(val) || isReactive(val)) {
    val = toValue(val)
  }
  const class_c = findClass(rel?.map || [], ch.model_name, change.path?.[0] || '')
  if ((!res || typeof res === 'function') && !isNaN(Number(val)) && Number(val) !== 0) {
    const numericVal = Number(val)
    if (
      class_c &&
      Object.prototype.hasOwnProperty.call(rel, class_c) &&
      Array.isArray(rel[class_c])
    ) {
      const class_entries = rel[class_c]
      if (class_c === 'Dropdowns') {
        const field_obj = class_entries.find(d => d.id === numericVal)

        if (field_obj) {
          val = field_obj.value
        }
      } else {
        const field_obj = class_entries.find(d => d.id === numericVal)
        if (field_obj) {
          if (class_c === 'Role') {
            val = field_obj?.caption
          } else if (class_c === 'Complaints'){
            val = field_obj?.qems_id
          } else {
            val = field_obj?.name
            if (field_obj?.full_name) val = field_obj.full_name
          }
        }
      }
    }
  }
  return val ?? ''
}

function findClass(relMap, modelName, fieldName) {
  const modelEntry = relMap.find(entry => entry.model === modelName)
  if (!modelEntry) return null

  const fieldEntry = modelEntry.fields.find(field => field.field === fieldName)
  return fieldEntry ? fieldEntry.class : null
}

function _pick({ history, related }) {
  const hist = []
  const h = props.limit ? history.slice(0, props.limit) : [...history]
  for (const batch of h) {
    const base = {
      date: commons.dateLocal(batch[0].date, 'Pp'),
      author: batch[0].fio || batch[0].username,
    }

    const items = batch.map(formatDiff, related).map(item => ({
      ...item,
      changes: item.changes.filter(ch => ch.action?.trim() || ch.text?.trim()),
    }))

    if (!items.length) continue

    const byEntity = new Map()
    for (const { entity, changes } of items) {
      const key = entity ?? '__none__'
      if (!byEntity.has(key)) {
        byEntity.set(key, { ...base, entity, changes: [] })
      }
      byEntity.get(key).changes.push(...changes)
    }

    hist.push(...byEntity.values())
  }
  return hist
}
</script>
